/**
 * The SSH console: shells on machines, rather than agents on projects.
 *
 * A separate screen because it answers a separate question. The cockpit asks
 * "what is the workforce doing"; this asks "what is happening on that box". They
 * share a wall — the same tiles, the same drag, the same layouts — because a
 * terminal is a terminal, and they share nothing else: no accounts, no models, no
 * safe boundaries, no waiting-for-you signal. A shell is never waiting for you in
 * a way software can see.
 *
 * The two walls are stored apart (`kimene.ssh.*`), so arranging one does not
 * rearrange the other and pruning one against its own session list cannot delete
 * the other's panes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import {
  GripVertical,
  ListChecks,
  CheckCheck,
  Plus,
  Radio,
  Search,
  Trash2,
  PanelLeftOpen,
  PanelLeftClose,
} from 'lucide-react'
import clsx from 'clsx'

import { api, type PageResponse } from '@/api/client'
import { Icon } from '@/components/icon'
import { Page, Button, StatusDot, EmptyState } from '@/components/primitives'
import {
  Dialog,
  Notice,
  QueryState,
  RowCheckbox,
  SelectField,
  TextField,
  CheckboxField,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { useT } from '@/i18n/context'
import { useOverview, useResizableColumns } from '@/routes/fleet-telemetry'
import { useWallLayout, usePresetLoad, type LayoutMode } from '@/hooks/useWallLayout'
import { useFitToScreen } from '@/hooks/useFitToScreen'
import { useRegisterWallFit } from '@/terminal/wallFit'
import { useDragAnnouncements, dragId, parseDragId } from '@/hooks/useManualOrder'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import { usePhoneLayout } from '@/hooks/usePointerKind'
import {
  LayoutTabs,
  SessionAge,
  SessionEnded,
  SortableSlot,
  WallControls,
  sessionLabel,
  type SessionRow,
} from '@/terminal/wall'
import { BroadcastProvider, useBroadcast } from '@/terminal/broadcast'
import { PaneChordProvider } from '@/terminal/paneChord'
import { CommandPalette, type PaletteCommand } from '@/terminal/palette'
import {
  StartupProvider,
  readTemplates,
  removeTemplate,
  upsertTemplate,
  writeTemplates,
  useStartupStore,
  type ShellTemplate,
} from '@/terminal/templates'
import { sessionActivity, activityLabelKey, type SessionActivity } from '@/lib/sessionActivity'
import { sessionActivityTone } from '@/lib/tone'
import { useNow } from '@/hooks/useElapsed'
import type { ID } from '@/types/domain'
import './LiveOpsPage.css'
import './HostsPage.css'

/** How many tiles a mode draws. The cockpit's reasoning, unchanged. */
const VISIBLE_TILES: Record<LayoutMode, number> = {
  single: 1,
  split: 2,
  grid: Number.POSITIVE_INFINITY,
}

export function TerminalsPage() {
  const t = useT()
  const confirm = useConfirm()
  const describe = useApiErrorMessage()
  const queryClient = useQueryClient()
  /** null = closed. Otherwise the form's starting point — a template the palette
   *  named, or just a host, or nothing. Offering "open a shell on SRV4" and then
   *  asking which host would be a poor answer. */
  const [opening, setOpening] = useState<Partial<ShellTemplate> | null>(null)
  const templates = readTemplates()
  const startup = useStartupStore()
  /**
   * Broadcasting, and deliberately not persisted.
   *
   * Every other arrangement on this screen outlives the tab, because coming back
   * to the wall you left is the point. This one must not: a broadcast mode an
   * operator does not remember turning on is how one command reaches seven
   * machines. It has to be switched on for the task and lost with the page.
   */
  const [casting, setCasting] = useState(false)
  const [palette, setPalette] = useState(false)

  /**
   * Folded away, and how wide.
   *
   * Same controls as the cockpit and the same shared hook, on this screen's own
   * keys. Sharing the cockpit's would mean folding one screen's list folded the
   * other's — the same reason the wall arrangements are kept apart.
   */
  const [leftOpen, setLeftOpen] = useState(
    () => localStorage.getItem('kimene.ssh.leftOpen') !== '0',
  )

  /**
   * On a phone the two columns are two *views*, exactly as on the cockpit.
   *
   * This screen shares the cockpit's layout classes on purpose — the two
   * layouts are the same layout — so it also shares the phone rules in
   * LiveOpsPage.css, and it has to share the state that tells those rules which
   * view is showing. Without it the shell list took the whole height and the
   * terminal column was left at its own padding, which is the same defect one
   * screen over.
   *
   * The state moves on a gesture and nothing else: opening a shell goes to the
   * terminal, and the reveal control comes back to the list.
   */
  const phone = usePhoneLayout()
  const [phoneView, setPhoneView] = useState<'list' | 'terminal'>('list')
  const listVisible = phone ? phoneView === 'list' : leftOpen
  const showList = useCallback(
    (on: boolean) => (phone ? setPhoneView(on ? 'list' : 'terminal') : setLeftOpen(on)),
    [phone],
  )
  useEffect(() => {
    localStorage.setItem('kimene.ssh.leftOpen', leftOpen ? '1' : '0')
  }, [leftOpen])
  const { widths, startDrag } = useResizableColumns({ storageKey: 'kimene.ssh.cols' })

  const [active, setActive] = useState<string | null>(null)

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<PageResponse<SessionRow>>('/sessions'),
    refetchInterval: 5_000,
  })

  // Only shells. The endpoint returns every session because one list is one
  // query; the screen is what narrows it, and narrowing here rather than adding a
  // provider parameter keeps the cockpit and this screen on one cache entry.
  const items = useMemo(
    () => (sessions.data?.items ?? []).filter((s) => s.provider === 'shell'),
    [sessions.data],
  )

  const {
    layout,
    setLayout,
    fit,
    setFit,
    panes,
    setPanes,
    selected,
    setSelected,
    manualOrder,
    setManualOrder,
    resetOrder,
    presets,
    activePreset,
    presetDirty,
    savePreset,
    loadPreset,
    deletePreset,
  } = useWallLayout(items, 'ssh')

  // The column the tiles stay inside, and the grid where the floor tokens and
  // the gap resolve. State rather than refs: the fit hook has to re-run when the
  // nodes arrive, and a ref assignment does not re-render.
  const [wallBox, setWallBox] = useState<HTMLElement | null>(null)
  const [wallGrid, setWallGrid] = useState<HTMLElement | null>(null)

  const presetLoad = usePresetLoad(loadPreset)

  const clearChecked = useCallback(() => setSelected([]), [setSelected])
  const { selecting, toggle: toggleSelecting } = useSelectionMode(selected, clearChecked)

  const now = useNow(1000)
  const at = sessions.dataUpdatedAt
  const activityOf = useCallback(
    (s?: SessionRow): SessionActivity =>
      sessionActivity({
        state: s?.state,
        kind: s?.kind,
        busyAgoMs: s?.busyAgoMs,
        working: s?.working,
        staleMs: at ? now - at : 0,
        attached: s?.attached,
      }),
    [now, at],
  )

  const ordered = useMemo(() => {
    if (!manualOrder) return items
    const rank = new Map(manualOrder.map((id, i) => [id, i]))
    return items
      .map((s, i) => ({ s, i, at: rank.get(s.id) ?? Number.MAX_SAFE_INTEGER }))
      .sort((a, b) => a.at - b.at || a.i - b.i)
      .map((r) => r.s)
  }, [items, manualOrder])

  const visible = useMemo(() => {
    const cap = VISIBLE_TILES[layout]
    if (panes.length <= cap) return panes
    const i = panes.findIndex((p) => p.id === active)
    if (i < 0) return panes.slice(0, cap)
    const start = Math.min(i, Math.max(0, panes.length - cap))
    return panes.slice(start, start + cap)
  }, [panes, layout, active])

  const listIds = useMemo(() => ordered.map((s) => dragId('list', s.id)), [ordered])
  const wallIds = useMemo(() => visible.map((p) => dragId('wall', p.id)), [visible])
  // The bus keys on session ids, the sortable registry on surface-prefixed ones.
  // Two lists rather than one stripped at the call site: a memo that produced the
  // wrong shape here would silently broadcast to nobody.
  const castMembers = useMemo(() => visible.map((p) => p.id), [visible])

  /** Grid cells, not tiles: a pane spanning two columns takes two. */
  const wallCells = useMemo(
    () => visible.reduce((n, p) => n + p.colSpan * p.rowSpan, 0),
    [visible],
  )
  /** The pane maximised over the viewport, if any — see the cockpit's own note:
   *  a maximised pane has the whole screen and nothing left to fit. */
  const [fullPane, setFullPane] = useState<string | null>(null)
  const { fit: fitState } = useFitToScreen({
    enabled: fit && layout === 'grid' && fullPane === null,
    cells: wallCells,
    box: wallBox,
    grid: wallGrid,
  })
  useRegisterWallFit({
    fittable: layout === 'grid' && fullPane === null,
    enabled: fit,
    setEnabled: setFit,
    font: fitState?.font ?? null,
    fits: fitState?.fits ?? true,
    columns: fitState?.columns ?? 0,
    rows: fitState?.rows ?? 0,
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const nameOf = useCallback(
    (id: string) => {
      const row = items.find((x) => x.id === id)
      return row ? sessionLabel(row, id) : id
    },
    [items],
  )
  const announcements = useDragAnnouncements(nameOf)

  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const from = parseDragId(String(e.active.id))
      const over = e.over ? parseDragId(String(e.over.id)) : null
      if (!over || (from.surface === over.surface && from.id === over.id)) return

      if (from.surface === 'wall' && over.surface === 'wall') {
        const a = panes.findIndex((p) => p.id === from.id)
        const b = panes.findIndex((p) => p.id === over.id)
        if (a < 0 || b < 0) return
        setPanes(arrayMove(panes, a, b))
        return
      }
      if (from.surface === 'list' && over.surface === 'wall') {
        if (panes.some((p) => p.id === from.id)) return
        const at2 = panes.findIndex((p) => p.id === over.id)
        const next = [...panes]
        next.splice(at2 < 0 ? next.length : at2, 0, { id: from.id, colSpan: 1, rowSpan: 1 })
        setPanes(next)
        return
      }
      if (from.surface !== 'list' || over.surface !== 'list') return
      const ids = ordered.map((x) => x.id)
      const a = ids.indexOf(from.id)
      const b = ids.indexOf(over.id)
      if (a < 0 || b < 0) return
      setManualOrder(arrayMove(ids, a, b))
    },
    [panes, setPanes, ordered, setManualOrder],
  )

  const openOnWall = (id: string) => {
    setActive(id)
    setPanes((prev) => (prev.some((p) => p.id === id) ? prev : [...prev, { id, colSpan: 1, rowSpan: 1 }]))
    // On a phone, opening a shell *is* the request to look at it. Guarded, or
    // this folds the sessions column on a desktop every time somebody opens a
    // shell — `showList` means two different things on the two layouts.
    if (phone) showList(false)
  }

  const stop = useMutation({
    mutationFn: (id: string) => api.del<void>(`/sessions/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  })

  const hosts = useOverview().data?.hosts ?? []

  /**
   * Everything this screen can do, as a list.
   *
   * Built from the same state the buttons read, rather than from a static table
   * of actions — a palette entry that calls a stale closure is a control that
   * lies, and this screen re-renders every second.
   */
  const commands = useMemo<PaletteCommand[]>(() => {
    const out: PaletteCommand[] = []
    for (const h of hosts) {
      out.push({
        id: `open:${h.id}`,
        label: t('palette.openOn', { host: h.name }),
        hint: h.status === 'ONLINE' ? undefined : h.status,
        group: t('palette.groupOpen'),
        run: () => setOpening({ hostId: h.id }),
      })
    }
    for (const sn of ordered) {
      const name = sessionLabel(sn, sn.id)
      const host = hosts.find((h) => h.id === sn.hostId)?.name
      const onWall = panes.some((p) => p.id === sn.id)
      out.push({
        id: `show:${sn.id}`,
        label: `${name} — ${onWall ? t('palette.hide') : t('palette.show')}`,
        hint: host,
        group: t('palette.groupPanes'),
        run: () =>
          onWall
            ? setPanes((prev) => prev.filter((p) => p.id !== sn.id))
            : openOnWall(sn.id),
      })
      out.push({
        id: `close:${sn.id}`,
        label: `${name} — ${t('palette.close')}`,
        hint: host,
        group: t('palette.groupPanes'),
        danger: true,
        run: () => void stop.mutate(sn.id),
      })
    }
    for (const tpl of templates) {
      const host = hosts.find((h) => h.id === tpl.hostId)?.name
      out.push({
        id: `tpl:${tpl.id}`,
        label: tpl.name,
        // What it will actually do, so a template is never a name whose effect
        // has to be remembered.
        hint: [host, tpl.command].filter(Boolean).join(' · '),
        group: t('tpl.group'),
        run: () => setOpening(tpl),
      })
    }
    out.push(
      { id: 'view:single', label: t('live.layoutSingle'), group: t('palette.groupView'), run: () => setLayout('single') },
      { id: 'view:split', label: t('live.layoutSplit'), group: t('palette.groupView'), run: () => setLayout('split') },
      { id: 'view:grid', label: t('live.layoutGrid'), group: t('palette.groupView'), run: () => setLayout('grid') },
      {
        id: 'view:cast',
        label: t(casting ? 'cast.off' : 'cast.on'),
        group: t('palette.groupView'),
        run: () => setCasting((c) => !c),
      },
    )
    return out
    // `hosts` and `ordered` are fresh arrays each poll; the effect of listing them
    // is a rebuild per second, which for a few dozen closures is nothing and is
    // cheaper than being wrong about which session a command names.
  }, [hosts, ordered, panes, casting, templates, t, setPanes, setLayout, stop])

  /**
   * F2, from anywhere on the screen including inside a terminal.
   *
   * The pane returns false for it so xterm never sends it on, but does not stop
   * the DOM event — so it arrives here by bubbling, and a pane on a screen with no
   * palette simply swallows one key. See the note in TerminalPane.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // F2, not F1: F1 is Help everywhere a keyboard has existed, and this
      // console had it opening a palette while its own key reference had no key
      // at all. The two swapped rather than one of them going without.
      if (e.key !== 'F2') return
      e.preventDefault()
      setPalette((open) => !open)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const closeSelected = async () => {
    const rows = items.filter((x) => selected.includes(x.id))
    const ok = await confirm({
      title: t('ssh.closeMany'),
      message: t('ssh.closeManyConfirm', { count: rows.length }),
      confirmLabel: t('ssh.closeMany'),
    })
    if (!ok) return
    for (const s of rows) await stop.mutateAsync(s.id).catch(() => undefined)
    setSelected([])
  }

  /* The one control the cockpit has no equivalent of. Passed into WallControls'
     slot rather than drawn beside it, so both places that render those controls —
     the column, and the strip that replaces it when the column folds — get the
     same row. */
  const castToggle = (
    <button
      type="button"
      className="k-lo__cast"
      aria-pressed={casting}
      onClick={() => setCasting((c) => !c)}
      title={t(casting ? 'cast.off' : 'cast.on')}
    >
      <Icon icon={Radio} size={14} />
      <span>{t('cast.on')}</span>
    </button>
  )

  return (
    <Page>
      {/* No page header, exactly as the cockpit has none, and for the same
          reason: this is a wall of terminals and a title bar costs a row of them.
          The heading still has to exist for anyone arriving by screen reader with
          no visual context — it is just not allowed to take height. */}
      <h1 className="k-visually-hidden">{t('ssh.title')}</h1>

      {palette && <CommandPalette onClose={() => setPalette(false)} commands={commands} />}

      {opening !== null && (
        <OpenShellDialog
          initial={opening}
          onClose={() => setOpening(null)}
          onOpened={(id, command) => {
            setOpening(null)
            // Armed before the pane exists, so the command is waiting when the
            // socket opens rather than racing it.
            if (command) startup.arm(id, command)
            void queryClient.invalidateQueries({ queryKey: ['sessions'] })
            openOnWall(id)
          }}
        />
      )}

      {stop.error && <Notice tone="err">{describe(stop.error).title}</Notice>}

      {presetLoad.lost.length > 0 && (
        <Notice tone="warn">
          {t('live.wallLost', {
            count: presetLoad.lost.length,
            names: presetLoad.lost.join(', '),
          })}{' '}
          <button type="button" className="k-notice__dismiss" onClick={presetLoad.dismiss}>
            {t('action.close')}
          </button>
        </Notice>
      )}

      <div className="k-lo">
        <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
          <div
            className={clsx(
              'k-lo__cols',
              // Which view is on screen, for the phone rules in LiveOpsPage.css —
              // this screen shares the cockpit's layout classes on purpose, so
              // it shares those rules and needs the state they read.
              // The list is conditionally rendered; the wall is
              // hidden with `display: none`, because unmounting a pane costs a
              // WebSocket and a full scrollback replay to put back.
              phone && listVisible && 'k-lo__cols--list',
            )}
          >
            {!listVisible && (
              // The collapsed strip keeps both the way back and the way to a new
              // shell. An action that disappears because a panel was folded is
              // one an operator cannot rely on.
              <div className="k-lo__stub">
                <button
                  type="button"
                  className="k-lo__reveal k-lo__reveal--start"
                  onClick={() => setOpening({})}
                  aria-label={t('ssh.open')}
                  title={t('ssh.open')}
                >
                  <Icon icon={Plus} size={16} />
                </button>
                {/* The palette's door survives the fold too. Its own rule: a
                    control that disappears with a panel is one an operator
                    cannot rely on, and F2 alone is not a door. */}
                <button
                  type="button"
                  className="k-lo__reveal"
                  onClick={() => setPalette(true)}
                  aria-label={t('palette.open')}
                  title={t('palette.open')}
                >
                  <Icon icon={Search} size={16} />
                </button>
                <button
                  type="button"
                  className="k-lo__reveal k-lo__reveal--left"
                  onClick={() => showList(true)}
                  aria-label={t('ssh.showShells')}
                  title={t('ssh.showShells')}
                >
                  <Icon icon={PanelLeftOpen} size={16} />
                </button>
              </div>
            )}
            {listVisible && (
              <>
            <section className="k-lo__col k-lo__sessions" style={{ width: widths.left }}>
              <div className="k-lo__colhead">
                <span className="k-lo__coltitle">{t('ssh.open.title')}</span>
                <span className="k-lo__colcount k-mono">{ordered.length}</span>
                {/* The palette's key is not guessable, so it also has a door —
                    and it goes here, in the same 28px recipe as the controls
                    beside it, rather than in a header this screen no longer has. */}
                <button
                  type="button"
                  className="k-lo__collapse"
                  onClick={() => setPalette(true)}
                  aria-label={t('palette.open')}
                  title={t('palette.open')}
                >
                  <Icon icon={Search} size={15} />
                </button>
                <button
                  type="button"
                  className="k-lo__collapse"
                  aria-pressed={selecting}
                  onClick={toggleSelecting}
                  aria-label={t(selecting ? 'order.selectDone' : 'order.select')}
                  title={t(selecting ? 'order.selectDone' : 'order.select')}
                >
                  <Icon icon={ListChecks} size={15} />
                </button>
                {selecting && (
                  <button
                    type="button"
                    className="k-lo__collapse"
                    onClick={() => setSelected(ordered.map((x) => x.id))}
                    aria-label={t('order.selectAllShown')}
                    title={t('order.selectAllShown')}
                  >
                    <Icon icon={CheckCheck} size={15} />
                  </button>
                )}
                <button
                  type="button"
                  className="k-lo__collapse"
                  onClick={() => showList(false)}
                  aria-label={t('ssh.hideShells')}
                  title={t('ssh.hideShells')}
                >
                  <Icon icon={PanelLeftClose} size={15} />
                </button>
              </div>

              <div className="k-lo__view">
                <WallControls
                  layout={layout}
                  onLayout={setLayout}
                  presets={presets}
                  onSave={(name) => savePreset(name, nameOf)}
                  onLoad={presetLoad.load}
                  onDelete={deletePreset}
                  canSave={panes.length > 0}
                  manualOrder={manualOrder}
                  onResetOrder={resetOrder}
                  extra={castToggle}
                />
              </div>
              {/* Full width rather than tucked into the header row, exactly as the
                  cockpit's does: the column resizes down to 208px, where a header
                  carrying a title, a count and four controls would truncate the
                  one part made of words. */}
              <Button
                variant="primary"
                size="sm"
                icon={<Icon icon={Plus} size={15} />}
                className="k-lo__start"
                onClick={() => setOpening({})}
              >
                {t('ssh.open')}
              </Button>

              <div className="k-lo__scroll">
                <QueryState
                  pending={sessions.isPending}
                  error={sessions.error}
                  onRetry={() => void sessions.refetch()}
                  empty={ordered.length === 0}
                  emptyTitle={t('ssh.empty')}
                  emptyHint={t('ssh.emptyHint')}
                >
                  <SortableContext items={listIds} strategy={verticalListSortingStrategy}>
                    <ul className="k-slist">
                      {ordered.map((s) => (
                        <ShellRow
                          key={s.id}
                          session={s}
                          activity={activityOf(s)}
                          active={active === s.id}
                          selecting={selecting}
                          selected={selected.includes(s.id)}
                          onSelect={(on) =>
                            setSelected((prev) =>
                              on ? [...prev, s.id] : prev.filter((x) => x !== s.id),
                            )
                          }
                          onOpen={() => openOnWall(s.id)}
                        />
                      ))}
                    </ul>
                  </SortableContext>
                </QueryState>
              </div>
            </section>

            <div
              className="k-lo__gutter"
              role="separator"
              aria-orientation="vertical"
              aria-label={t('ssh.resize')}
              onPointerDown={(e) => startDrag('left', e)}
            />
              </>
            )}

            <StartupProvider value={startup}>
            <BroadcastProvider enabled={casting} members={castMembers}>
            <section className="k-lo__col k-lo__work" aria-label={t('ssh.wall')}>
              {/* The same strip the cockpit carries, for the same reason: a
                  shell wall gets unreadable at the same width an agent wall
                  does. Two copies of it would have drifted within a week. */}
              <LayoutTabs
                presets={presets}
                activeId={activePreset}
                dirty={presetDirty}
                onLoad={presetLoad.load}
                onSave={(name) => savePreset(name, nameOf)}
                onDelete={deletePreset}
                canSave={panes.length > 0}
              />
              {/* The view controls live in the shells column, where they cost no
                  terminal height — but a control that folds away with a panel is
                  one an operator cannot rely on. So when the column is folded
                  they come back here rather than disappearing with it. */}
              {!listVisible && (
                <div className="k-wall__head">
                  <WallControls
                    layout={layout}
                    onLayout={setLayout}
                    presets={presets}
                    onSave={(name) => savePreset(name, nameOf)}
                    onLoad={presetLoad.load}
                    onDelete={deletePreset}
                    canSave={panes.length > 0}
                    manualOrder={manualOrder}
                    onResetOrder={resetOrder}
                    extra={castToggle}
                  />
                </div>
              )}
              {selecting && selected.length > 0 && (
                <div className="k-bulkbar" role="region" aria-label={t('fleet.bulkTitle')}>
                  <span className="k-bulkbar__count">
                    {t('fleet.bulkSelected', { count: selected.length })}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPanes(selected.map((id) => ({ id, colSpan: 1, rowSpan: 1 })))
                      setLayout('grid')
                    }}
                  >
                    {t('live.bulkShowInGrid')}
                  </Button>
                  <Button size="sm" busy={stop.isPending} onClick={() => void closeSelected()}>
                    {t('ssh.closeMany')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
                    {t('fleet.bulkClear')}
                  </Button>
                </div>
              )}
              {casting && (
                /* A standing banner, not a toast: this is a mode, and the whole
                   risk of it is forgetting it is on. It says how many panes will
                   receive — including zero, which is the state where an operator
                   types confidently into nothing. */
                <CastBanner onStop={() => setCasting(false)} />
              )}
              <div className="k-lo__termwrap" ref={setWallBox}>
                {/* The seats are the same list the broadcast bus uses: what is
                    drawn, in the order it is drawn. Two lists would let a digit
                    name one tile while a relay named another. */}
                <PaneChordProvider panes={castMembers} onPick={setActive}>
                <SortableContext items={wallIds} strategy={rectSortingStrategy}>
                  <div className={`k-termregion k-termregion--${layout}`} ref={setWallGrid}>
                    {visible.length === 0 ? (
                      <div className="k-termregion__empty">
                        <EmptyState title={t('ssh.wallEmpty')} hint={t('ssh.wallEmptyHint')} />
                      </div>
                    ) : (
                      visible.map((pane) => (
                        <SortableSlot
                          key={pane.id}
                          pane={pane}
                          session={items.find((x) => x.id === pane.id)}
                          activity={activityOf(items.find((x) => x.id === pane.id))}
                          onDetach={() => setPanes((prev) => prev.filter((x) => x.id !== pane.id))}
                          onFocus={() => setActive(pane.id)}
                          onResize={(colSpan, rowSpan) =>
                            setPanes((prev) =>
                              prev.map((x) => (x.id === pane.id ? { ...x, colSpan, rowSpan } : x)),
                            )
                          }
                          onInspect={() => undefined}
                          onFullscreen={(on) =>
                            setFullPane((cur) => (on ? pane.id : cur === pane.id ? null : cur))
                          }
                          inspecting={false}
                          active={active === pane.id}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
                </PaneChordProvider>
              </div>
            </section>
            </BroadcastProvider>
            </StartupProvider>
          </div>
        </DndContext>
      </div>
    </Page>
  )
}

/** One shell in the list: which machine, and how long it has been open. */
function ShellRow({
  session,
  activity,
  active,
  selecting,
  selected,
  onSelect,
  onOpen,
}: {
  session: SessionRow
  activity: SessionActivity
  active: boolean
  selecting: boolean
  selected: boolean
  onSelect: (on: boolean) => void
  onOpen: () => void
}) {
  const t = useT()
  const hosts = useOverview().data?.hosts ?? []
  const host = hosts.find((h) => h.id === session.hostId)
  const label = sessionLabel(session, session.id)
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: dragId('list', session.id),
  })

  return (
    <li
      ref={setNodeRef}
      className={clsx(
        'k-slist__item',
        selected && 'k-slist__item--sel',
        isDragging && 'k-slist__item--dragging',
      )}
      style={{ transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined }}
    >
      <span className="k-slist__sel" onClick={(e) => e.stopPropagation()}>
        {selecting ? (
          <RowCheckbox checked={selected} onChange={onSelect} label={label} />
        ) : (
          <span
            className="k-slist__grip"
            aria-label={t('order.move', { name: label })}
            title={t('order.move', { name: label })}
            {...attributes}
            {...listeners}
          >
            <Icon icon={GripVertical} size={13} />
          </span>
        )}
      </span>
      <button
        type="button"
        className={clsx(
          'k-slist__row',
          // The stripe down the leading edge. It was missing here, so a shell's
          // row said what it was doing with one 7px dot while the cockpit's said
          // it with the whole row — the same state reading differently on two
          // screens is exactly what a shared tone token exists to prevent.
          `k-slist__row--${sessionActivityTone(activity)}`,
          active && 'k-slist__row--on',
        )}
        onClick={onOpen}
        aria-current={active}
      >
        {/* Both facts, as in the cockpit: what the colour means, and the
            lifecycle state it does not replace. */}
        <StatusDot
          tone={sessionActivityTone(activity)}
          live={activity === 'WORKING'}
          label={`${t(activityLabelKey(activity))} · ${session.state}`}
        />
        <span className="k-slist__main">
          <span className="k-slist__l1">
            <span className="k-slist__name">{label}</span>
            <SessionAge session={session} />
          </span>
          <span className="k-slist__l2">
            <span className="k-mono">{host?.name ?? session.hostId.slice(0, 8)}</span>
            {session.workingDir && (
              <>
                <span className="k-slist__sep">·</span>
                <span className="k-mono">{session.workingDir}</span>
              </>
            )}
            {/* A closed shell stays listed for a few hours, because tmux keeps
                its pane and the reason it ended is still readable in it. */}
            <SessionEnded session={session} />
          </span>
        </span>
      </button>
    </li>
  )
}

/**
 * Opening a shell: which machine, where, and whether to keep a transcript.
 *
 * The recording box is unticked and says what it means. A shell's transcript is
 * raw terminal bytes, so it captures whatever is typed into it — including a
 * password pasted at a prompt — and redaction lives at the log boundary, not in a
 * recording. An operator who wants the record should be choosing it.
 */
function OpenShellDialog({
  initial,
  onClose,
  onOpened,
}: {
  /** What to start the form with. A template, or just a host the palette named. */
  initial?: Partial<ShellTemplate>
  onClose: () => void
  onOpened: (sessionId: string, command: string) => void
}) {
  const t = useT()
  const describe = useApiErrorMessage()
  const hosts = useOverview().data?.hosts ?? []
  // A seed, not an override: the dialog is mounted fresh each time it opens, so
  // there is no server value that could arrive later and take back a choice.
  const [hostId, setHostId] = useState<ID>((initial?.hostId as ID) ?? '')
  const [dir, setDir] = useState(initial?.workingDir ?? '')
  const [label, setLabel] = useState('')
  const [record, setRecord] = useState(false)
  const [command, setCommand] = useState(initial?.command ?? '')
  const [saveAs, setSaveAs] = useState('')
  /** Read once and held, so deleting one is visible without the dialog being
   *  closed and reopened. The store is the truth; this is the view of it. */
  const [saved, setSaved] = useState<ShellTemplate[]>(() => readTemplates())

  /**
   * Whether this deployment records at all.
   *
   * Offered only when it does. `startRecorder` returns early with no directory,
   * so the box would tick and no file would exist — the same shape of defect as
   * offering a recording nobody could read, one step earlier. Absent means the
   * server is older than the field, and the honest default there is to keep
   * offering it rather than to hide a working feature.
   */
  const system = useQuery({
    queryKey: ['system'],
    queryFn: () => api.get<{ capabilities?: { terminalRecording?: boolean } }>('/system'),
    staleTime: 5 * 60_000,
  })
  const recordable = system.data?.capabilities?.terminalRecording !== false

  const open = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/sessions/shell', {
        hostId,
        workingDir: dir.trim() || undefined,
        label: label.trim() || undefined,
        record,
      }),
    onSuccess: (s) => {
      if (saveAs.trim())
        writeTemplates(
          upsertTemplate(readTemplates(), {
            id: '',
            name: saveAs,
            hostId,
            workingDir: dir.trim() || undefined,
            command: command.trim() || undefined,
          }),
        )
      onOpened(s.id, command.trim())
    },
  })

  const reachable = hosts.filter((h) => h.status === 'ONLINE')

  return (
    <Dialog
      title={t('ssh.open')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            busy={open.isPending}
            disabled={!hostId}
            onClick={() => open.mutate()}
          >
            {t('ssh.open')}
          </Button>
        </>
      }
    >
      <div className="k-form">
        {saved.length > 0 && (
          /* The templates, where they are made. There was no way to load one from
             here and no way to delete one at all — every template ever saved was
             permanent, including the one saved by a typo. */
          <div className="k-tpl">
            <p className="k-tpl__title">{t('tpl.group')}</p>
            <ul className="k-tpl__list">
              {saved.map((tpl) => (
                <li key={tpl.id} className="k-tpl__row">
                  <button
                    type="button"
                    className="k-tpl__use"
                    onClick={() => {
                      setHostId(tpl.hostId as ID)
                      setDir(tpl.workingDir ?? '')
                      setCommand(tpl.command ?? '')
                    }}
                  >
                    <span className="k-tpl__name">{tpl.name}</span>
                    {tpl.command && <span className="k-tpl__cmd k-mono">{tpl.command}</span>}
                  </button>
                  <button
                    type="button"
                    className="k-tpl__del"
                    aria-label={t('tpl.delete', { name: tpl.name })}
                    title={t('tpl.delete', { name: tpl.name })}
                    onClick={() => {
                      const next = removeTemplate(saved, tpl.id)
                      writeTemplates(next)
                      setSaved(next)
                    }}
                  >
                    <Icon icon={Trash2} size={13} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <SelectField
          label={t('ssh.host')}
          value={hostId}
          onChange={(v) => setHostId(v as ID)}
          options={[
            { value: '', label: t('ssh.hostPick') },
            ...reachable.map((h) => ({ value: h.id, label: h.name })),
          ]}
        />
        <TextField
          label={t('ssh.label')}
          hint={t('ssh.labelHint')}
          value={label}
          onChange={setLabel}
        />
        <TextField
          label={t('ssh.workingDir')}
          hint={t('ssh.workingDirHint')}
          mono
          value={dir}
          onChange={setDir}
        />
        <TextField
          label={t('tpl.command')}
          hint={t('tpl.commandHint')}
          mono
          value={command}
          onChange={setCommand}
        />
        <TextField
          label={t('tpl.saveAs')}
          hint={t('tpl.saveAsHint')}
          value={saveAs}
          onChange={setSaveAs}
        />
        {recordable && (
        <CheckboxField
          label={t('ssh.record')}
          hint={t('ssh.recordHint')}
          checked={record}
          onChange={setRecord}
        />
        )}
        {hosts.length > 0 && reachable.length === 0 && (
          <Notice tone="warn">{t('ssh.noHosts')}</Notice>
        )}
        {open.error && <Notice tone="err">{describe(open.error).title}</Notice>}
      </div>
    </Dialog>
  )
}


/**
 * What is listening, said where the operator is looking.
 *
 * Its own component only so it can read the bus, which is the single owner of
 * which panes are muted — a copy of that state on the page would be a second
 * answer to a question that must have one.
 */
function CastBanner({ onStop }: { onStop: () => void }) {
  const t = useT()
  const { count } = useBroadcast()
  return (
    <div className="k-cast" role="status">
      <Icon icon={Radio} size={15} />
      <strong>{count > 0 ? t('cast.count', { n: count }) : t('cast.none')}</strong>
      <span className="k-cast__hint">{t('cast.hint')}</span>
      <Button size="sm" variant="ghost" onClick={onStop}>
        {t('cast.off')}
      </Button>
    </div>
  )
}
